import type { RJSFSchema, UiSchema } from '@rjsf/utils';

export type UiFieldSpec = {
  component?:
    | 'text'
    | 'textarea'
    | 'password'
    | 'number'
    | 'slider'
    | 'select'
    | 'multiselect'
    | 'checkbox'
    | 'radio'
    | 'json'
    | 'code'
    | 'color'
    | 'keyvalue';
  label?: string;
  placeholder?: string;
  help?: string;
  options?: Array<string | number> | Array<{ label: string; value: string | number }>;
  min?: number;
  max?: number;
  step?: number;
  rows?: number;
  height?: number;
  minHeight?: number;
  maxHeight?: number;
  language?: string;
  lineNumbers?: boolean;
  width?: number | string;
  format?: string;
  secret?: boolean;
  order?: number;
  group?: string;
};

export type UiFormSpec = {
  tabs?: Array<{ id: string; label: string; fields: string[] }>;
  sections?: Array<{ id: string; label?: string; fields: string[] }>;
};

const clone = <T,>(x: T): T => JSON.parse(JSON.stringify(x ?? {}));

export function uiHintsToRjsf(
  schemaIn: RJSFSchema,
  attrsUi?: Record<string, UiFieldSpec>,
  formUi?: UiFormSpec
): { uiSchema: UiSchema; patchedSchema: RJSFSchema } {
  const ui: UiSchema = { 'ui:submitButtonOptions': { norender: true } };
  const schema = clone(schemaIn || { type: 'object', properties: {} });

  const props: Record<string, any> =
    schema && schema.type === 'object' && schema.properties ? (schema.properties as any) : {};

  const fieldNames = Object.keys(props);

  const setOneOfFromOptions = (name: string, opts: UiFieldSpec['options']) => {
    if (!opts || !Array.isArray(opts) || opts.length === 0) return;
    const oneOf = (opts as any[]).map((o) =>
      typeof o === 'object' && o && 'value' in o
        ? { const: (o as any).value, title: (o as any).label ?? String((o as any).value) }
        : { const: o, title: String(o) },
    );
    props[name] = { ...(props[name] || {}), oneOf };
  };

  for (const name of fieldNames) {
    const specRaw = attrsUi?.[name] as UiFieldSpec | (UiFieldSpec & Record<string, any>) | undefined;
    const spec = specRaw ?? undefined;
    const conf: Record<string, any> = {};

    const assignOption = (key: string, value: unknown) => {
      if (value === undefined) return;
      conf['ui:options'] = { ...(conf['ui:options'] || {}), [key]: value };
    };

    if (spec && 'label' in spec && spec.label) props[name] = { ...(props[name] || {}), title: spec.label };
    if (spec && 'format' in spec && spec.format && !props[name]?.format) props[name] = { ...(props[name] || {}), format: spec.format };

    const componentHint = typeof (spec as any)?.component === 'string'
      ? (spec as any).component
      : typeof (spec as any)?.['ui:widget'] === 'string'
        ? (spec as any)['ui:widget']
        : typeof (spec as any)?.widget === 'string'
          ? (spec as any).widget
          : undefined;
    const component = componentHint ? componentHint.toLowerCase() : undefined;

    const isSlider = component === 'slider';
    if (spec && 'min' in spec && (spec as any).min != null) {
      if (isSlider) assignOption('min', (spec as any).min);
      else props[name] = { ...(props[name] || {}), minimum: (spec as any).min };
    }
    if (spec && 'max' in spec && (spec as any).max != null) {
      if (isSlider) assignOption('max', (spec as any).max);
      else props[name] = { ...(props[name] || {}), maximum: (spec as any).max };
    }
    if (spec && 'step' in spec && (spec as any).step != null) assignOption('step', (spec as any).step);

    // Allow passthrough of common editor hints
    if (spec && 'rows' in spec && (spec as any).rows != null) assignOption('rows', (spec as any).rows);
    if (spec && 'height' in spec && (spec as any).height != null) assignOption('height', (spec as any).height);
    if (spec && 'minHeight' in spec && (spec as any).minHeight != null) assignOption('minHeight', (spec as any).minHeight);
    if (spec && 'maxHeight' in spec && (spec as any).maxHeight != null) assignOption('maxHeight', (spec as any).maxHeight);
    if (spec && 'lineNumbers' in spec && (spec as any).lineNumbers != null) assignOption('lineNumbers', (spec as any).lineNumbers);
    if (spec && 'width' in spec && (spec as any).width != null) assignOption('width', (spec as any).width);

    if (spec && 'placeholder' in spec && (spec as any).placeholder) conf['ui:placeholder'] = (spec as any).placeholder;
    if (spec && 'help' in spec && (spec as any).help) conf['ui:help'] = (spec as any).help;

    // component → widget mapping
    const widget = (() => {
      const c = component;
      if (!c && (spec as any)?.secret) return 'password';
      switch (c) {
        case 'password':
          return 'password';
        case 'number':
        case 'updown':
          return 'updown';
        case 'slider':
          return 'slider';
        case 'select':
          return 'select';
        case 'multiselect':
          assignOption('multiple', true);
          return 'select';
        case 'checkbox':
          return 'checkbox';
        case 'radio':
          return 'radio';
        case 'textarea':
          return 'textarea';
        case 'json': {
          assignOption('language', (spec as any)?.language ?? 'json');
          const t = (props[name] as any)?.type;
          if (t === 'object' || t === 'array') {
            (conf as any)['ui:field'] = 'json';
            return undefined;
          }
          return 'code';
        }
        case 'code':
        case 'codeeditor':
          if ((spec as any)?.language) assignOption('language', (spec as any).language);
          return 'code';
        case 'jsoneditor':
          assignOption('language', (spec as any)?.language ?? 'json');
          (conf as any)['ui:field'] = 'json';
          return undefined;
        case 'color':
          return 'color';
        case 'textarea-autosize':
          assignOption('autosize', true);
          return 'textarea';
        case 'switch':
          return 'checkbox';
        case 'keyvalue':
          return undefined;
        default:
          return undefined;
      }
    })();
    if (widget) conf['ui:widget'] = widget;

    // Merge explicit ui:options from spec
    const rawUiOptions = (spec as any)?.['ui:options'];
    if (rawUiOptions && typeof rawUiOptions === 'object') {
      conf['ui:options'] = { ...(conf['ui:options'] || {}), ...rawUiOptions };
    }

    // options → schema.oneOf (preferred in RJSF v5) if not already defined
    if (spec && 'options' in spec && (spec as any).options && !props[name]?.enum && !props[name]?.oneOf) {
      setOneOfFromOptions(name, (spec as any).options);
    }

    if (Object.keys(conf).length) (ui as any)[name] = conf;
  }

  // ordering: tabs first (or sections), then rest alphabetically
  const formOrder: string[] = [];
  const add = (arr?: string[]) => {
    if (!arr) return;
    for (const f of arr) if (!formOrder.includes(f)) formOrder.push(f);
  };
  if (formUi?.tabs?.length) formUi.tabs.forEach((t) => add(t.fields));
  else if (formUi?.sections?.length) formUi.sections.forEach((s) => add(s.fields));
  for (const name of fieldNames.sort()) if (!formOrder.includes(name)) formOrder.push(name);
  if (formOrder.length) (ui as any)['ui:order'] = formOrder;

  return { uiSchema: ui, patchedSchema: { ...schema, properties: props } };
}
